<template>
    <div class="card-form">
        <span
            v-if="formError"
            class="form-error form-global-error"
        >
            {{ formError }}
        </span>
        <div v-show="!hideCard" ref="card" />
        <span
            v-if="cardError"
            class="form-error"
        >
            {{ cardError }}
        </span>
        <fieldset
            class="coupon-field"
            :class="{ error: couponFieldError, hidden: !couponField, success: couponSuccess, noCard: freeSubscription }"
        >
            <input
                v-model="coupon"
                v-debounce:500="validateCoupon"
                placeholder="Coupon"
                type="text"
                required=""
                autocomplete="off"
                :class="{ error: couponFieldError, success: couponSuccess }"
            >
            <label>
                Enter your coupon code
            </label>
        </fieldset>
        <span
            v-if="couponFieldError"
            class="form-error coupon-error"
        >
            {{ couponFieldError }}
        </span>
        <div class="payment">
            <template v-if="!update && !upgradeLifetime">
                <base-submit-button
                    :disabled="couponFieldError || cardError || formError"
                    :class="{fullWidth: couponField }"
                    class="btn"
                    :loading="formProcessing"
                    @click="processPayment"
                >
                Purchase
                </base-submit-button>
                <span
                    v-if="!couponField"
                    class="coupon-btn"
                    @click="showCoupon"
                >
                    Have a code?
                </span>
            </template>
            <template v-else-if="update">
                <base-submit-button
                    :disabled="couponFieldError || cardError || formError"
                    :class="{ fullWidth: couponField }"
                    class="btn btn-update"
                    :loading="formProcessing"
                    @click="processUpdateForm"
                >
                Save changes
                </base-submit-button>
                <button
                    class="btn btn-update btn-secondary"
                    @click="closeUpdateModal"
                >
                    Cancel
                </button>
            </template>
        </div>
    </div>
</template>

<script>
import style from '@/utils/form/stripeElements';
import { loadScript, removeScript } from '@/utils/scriptLoader';
import apiCall from '@/utils/apiCall';
import subscribeUser from '@/utils/user/subscribeUser';
import { mapState, mapActions, mapMutations } from 'vuex';
import toasts from '@/mixins/toasts';
import getActiveSubscription from '@/utils/getActiveSubscription';

export default {
    mixins: [toasts],

    props: {
        planId: {
            type: String,
            default: '',
        },
        skipRegistration: {
            type: Boolean,
        },
        update: {
            type: Boolean,
            default: false,
        },
        upgradeUser: {
            type: Boolean,
            default: false,
        },
        upgradeOption: {
            type: Boolean,
            default: false,
        },
        upgradeLifetime: {
            type: Boolean,
            default: false,
        },
        processLifetimeUpgrade: {
            type: Boolean,
            default: false,
        },
        activeSites: {
            type: Number,
            default: 1,
        },
    },

    data: () => ({
        cardError: null,
        stripeSecret: null,
        formProcessing: false,
        couponField: false,
        couponFieldError: null,
        couponSuccess: false,
        coupon: null,
        discount: null,
        formError: null,
        hideCard: false,
        freeSubscription: false,
        stripe: {
            instance: null,
            card: null,
            elements: null,
        },
    }),

    computed: {
        ...mapState(['user', 'registeringUser', 'userSubscription']),
    },

    watch: {
        /**
         * Clears coupon options
         *
         * @param {string} newVal New input value.
         * @param {string} oldVal Old input value.
         */
        coupon(newVal, oldVal) {
            if ('' !== oldVal && '' === newVal) {
                this.hideCard = false;
                this.freeSubscription = false;
                this.$emit('update-coupon', { type: null, amount: null });
            }
        },

        /**
         * Watch state registeringUser flag change
         *
         * @param   {boolean}  newVal  New state state value
         *
         */
        async registeringUser(newVal) {
            if (false === newVal) {
                this.formProcessing = true;
                await this.loadUser();
                this.processProUserSubscription();
            }
        },

        /**
         * Watch lifetime upgrade action
         *
         * @param {boolean} newVal New component state value
         */
        processLifetimeUpgrade(newVal) {
            if (true === newVal) {
                this.processUpgradeForm();
            }
        },
    },

    /**
     * Mounted hook
     *
     */
    mounted() {
        this.getStripeSecret();

        loadScript('js.stripe.com/v3/', 'stripe', function () {
            this.configureStripe();
        }.bind(this));
    },

    /**
     * Before destroy hook
     */
    beforeDestroy() {
        removeScript('stripe');
    },

    methods: {
        ...mapActions([
            'loadUser',
            'loadLicenses',
            'reloadPaymentMethods',
            'loadUserSubscription',
        ]),

        ...mapMutations(['changeUIState', 'couponDiscount']),

        /**
         * Gets secret
         */
        async getStripeSecret() {
            const response = await apiCall(this, 'get', `${this.$appUrl}/api/billing/intent`);
            const { status, data } = response;

            if (200 === status) {
                this.stripeSecret = data.client_secret;
            } else {
                this.hideCard = true;
                this.formError = data.message;
            }
        },

        /**
         * Configures stripe
         *
         */
        configureStripe() {
            // eslint-disable-next-line no-undef
            this.stripe.instance = Stripe(process.env.MIX_STRIPE_KEY);
            this.stripe.elements = this.stripe.instance.elements();
            this.stripe.card = this.stripe.elements.create('card', style);
            this.stripe.card.mount(this.$refs.card);

            this.validateCard();
        },

        /**
         * Validates coupon code
         *
         */
        async validateCoupon() {
            if (this.coupon) {
                this.formProcessing = true;

                const { status, data } = await apiCall(this, 'get', `${this.$appUrl}/api/billing/coupons/${this.coupon}`);

                if (404 === status) {
                    this.couponFieldError = data;
                    this.couponSuccess = false;
                    this.hideCard = false;
                    this.freeSubscription = false;

                    this.$emit('update-coupon', { type: null, amount: null });
                } else {
                    this.couponFieldError = null;
                    this.couponSuccess = true;

                    if (data.percent_off) {
                        this.$emit('update-coupon', { type: 'discountInPrecent', amount: data.percent_off });
                    } else if (data.amount_off) {
                        this.$emit('update-coupon', { type: 'discountInCash', amount: data.amount_off });
                    }
                }
            } else {
                this.couponFieldError = null;
                this.couponSuccess = false;

                this.$emit('update-coupon', { type: null, amount: null });
            }
        },

        /**
         * Checks if coupon gives full discount
         *
         * @param   {number}  number  Precent of discount.
         *
         */
        fullDiscount(number) {
            if (100 === number) {
                this.hideCard = true;
                this.freeSubscription = true;
            } else {
                this.hideCard = false;
                this.freeSubscription = false;
                this.$emit('update-coupon', { type: null, amount: null });
            }
        },

        /**
         * Validates card data
         *
         */
        validateCard() {
            this.stripe.card.addEventListener('change', (e) => {
                if (e.error) {
                    this.cardError = e.error.message;
                    this.formProcessing = false;
                } else {
                    this.cardError = null;
                }
            });
        },

        /**
         * Shows coupon field
         */
        showCoupon() {
            this.couponField = true;
        },

        /**
         * Gets Stripe setup intent
         *
         * @returns {Promise} Stripe Setup Intent
         */
        getSetupIntent() {
            const payment = {
                payment_method: {
                    card: this.stripe.card,
                    billing_details: this.user.name,
                },
            };

            this.formProcessing = true;

            return this.stripe.instance.confirmCardSetup(this.stripeSecret, payment);
        },

        /**
         * Processes payment form
         *
         */
        processPayment() {
            if (this.upgradeUser || this.upgradeLifetime) {
                this.processUpgradeForm();
                return;
            }

            this.$cookies.remove('user_registration_details', null);

            if (this.skipRegistration) {
                this.processProUserSubscription();
            } else {
                this.formProcessing = true;
                this.$event.$emit('registerUser');
            }
        },

        /**
         * Processes registered pro user subscription
         *
         */
        async processProUserSubscription() {
            const paymentInitialized = await apiCall(this, 'put', '/api/user/meta', { payment_initialized: Date.now() });

            if (paymentInitialized) {
                if (this.freeSubscription) {
                    subscribeUser(this, this.planId, null, this.coupon);
                } else {
                    this.processPaidSubscriptionForm();
                }
            }
        },

        /**
         * Process paid subscription
         *
         */
        async processPaidSubscriptionForm() {
            const { setupIntent, error } = await this.getSetupIntent();

            if (error) {
                this.formProcessing = false;
                this.cardError = error.message;
            } else {
                const { payment_method } = setupIntent;

                subscribeUser(this, this.planId, payment_method, this.coupon);
            }
        },

        /**
         * Processes update card request
         */
        async processUpdateForm() {
            const { setupIntent, error } = await this.getSetupIntent();

            if (error) {
                this.formProcessing = false;
                this.toastError({
                    message: {
                        title: error.message,
                    },
                });
            } else {
                const { payment_method } = setupIntent;

                this.updatePaymentMethod(payment_method);
            }
        },

        /**
         * Processes upgrade form
         */
        async processUpgradeForm() {
            if (this.freeSubscription) {
                this.upgradeUserFreeSubscription();
            } else {
                this.upgradeUserSubscription();
            }
        },

        /**
         * Upgrades free user with 100% discount
         */
        async upgradeUserFreeSubscription() {
            const data = {
                plan: this.planId,
                payment_method: null,
                coupon: this.coupon,
                quantity: this.activeSites,
            };

            const { status } = await apiCall(this, 'post', '/api/billing/subscriptions/upgrade', data);

            if (200 === status) {
                this.formProcessing = true;

                try {
                    await this.loadLicenses(true);
                    await this.loadUserSubscription({ force: true });

                    this.formProcessing = false;

                    this.$emit('process-to-step');
                } catch {
                    this.formProcessing = false;

                    this.toastError({
                        message: {
                            title: 'An error occured, we couldn\'t upgrade your your account.<br> Please contact us or try again later',
                        },
                    });
                }
            }
        },

        /**
         * Upgrades free user
         */
        async upgradeUserSubscription() {
            const { setupIntent, error } = await this.getSetupIntent();

            // eslint-disable-next-line require-jsdoc
            const errorNotification = () => {
                return this.toastError({
                    message: {
                        title: 'An error occured, we couldn\'t upgrade your your account.<br> Please contact us or try again later',
                    },
                });
            };

            if (error) {
                this.formProcessing = false;
                this.$event.$emit('upgradeLifetimeError');

                this.toastError({
                    message: {
                        title: error.message,
                    },
                });

                this.$event.$emit('upgradeLifetimeError');
            } else {
                const { payment_method } = setupIntent;

                const { status } = await apiCall(this, 'post', '/api/billing/payments/methods', { payment_method });

                if (200 !== status) {
                    errorNotification();
                    this.$event.$emit('upgradeLifetimeError');

                    return;
                }

                const data = {
                    plan: this.planId,
                    payment_method,
                    coupon: this.coupon,
                    quantity: this.activeSites,
                };

                const response = await apiCall(this, 'post', '/api/billing/subscriptions/upgrade', data);

                if (200 === response.status) {
                    this.formProcessing = true;

                    try {
                        await this.loadLicenses(true);
                        await this.reloadPaymentMethods();
                        await this.loadUserSubscription({ force: true });

                        this.formProcessing = false;
                        this.$emit('process-to-step');
                    } catch {
                        errorNotification();
                        this.$event.$emit('upgradeLifetimeError');
                    }
                } else {
                    this.formProcessing = false;
                    this.$event.$emit('upgradeLifetimeError');
                    errorNotification();
                }
            }
        },

        /**
         * Updates pro user subscription
         *
         *  @param { string } payment_method Stripe payment method.
         *
         * @returns {Promise} Update subscription API call.
         */
        async updateUserSubscription(payment_method) {
            const { id } = this.userSubscription.plan;
            const { quantity } = this.userSubscription.items.data[0];

            const updateData = {
                plan: id,
                quantity: quantity,
                payment_method: payment_method,
            };

            const subscription = getActiveSubscription(this.user.subscriptions);

            return apiCall(this, 'put', `/api/billing/subscriptions/${subscription.id}`, updateData);
        },

        /**
         * Updates pro user payment method
         *
         * @param { string } payment_method Stripe payment method.
         */
        async updatePaymentMethod(payment_method) {
            const { status } = await apiCall(this, 'post', '/api/billing/payments/methods', { payment_method });

            if (200 === status) {
                this.formProcessing = true;

                this.updateUserSubscription(payment_method)
                    .then(() => {
                        this.reloadPaymentMethods();

                        this.toastSuccess({
                            message: {
                                title: 'Your Payment Method has been updated',
                            },
                        });

                        this.closeUpdateModal();
                    })
                    .catch(() => {
                        this.formProcessing = false;

                        this.toastError({
                            message: {
                                title: 'An error occured, we couldn\'t update your payment method',
                            },
                        });
                    });
            } else {
                this.formProcessing = false;

                this.toastError({
                    message: {
                        title: 'An error occured, we couldn\'t update your payment method',
                    },
                });
            }
        },

        /**
         * Closes update modal
         */
        closeUpdateModal() {
            if (this.upgradeOption) {
                this.$emit('switch-to-card-form');
            } else {
                this.changeUIState({ isUpdateModalVisible: false });
            }
        },
    },
};
</script>
